// Lib
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

// Utils
import quickNotesCanDrop from '../header/workspaceToolsHeader/quickNotes/popup/quickNotesCanDrop';

// Components
import OrchestratedList from '../../list/OrchestratedList';
import QuickNotesEmptyPlaceholder from './QuickNotesEmptyPlaceholder';
import SelectionMarquee from '../../canvas/selection/SelectionMarquee';
import List from '../../list/List';

// Hooks
import { useZoomContext } from '../../canvas/zoom/ZoomStateContext';
import useRenderListPlaceholders from '../../element/elementPlaceholder/useRenderListPlaceholders';
import useSelectionMarqueeList from '../../canvas/selection/useSelectionMarqueeList';

// Actions
import { refreshIfModified } from '../../element/board/boardService';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { IN_LIST_MODE } from '../../../common/inList/listTypes';

import './QuickNotesScrollableList.scss';

const INITIAL_RENDER_SIZE = 10;

type Props = {
    quickNotesRootId: string;
    quickNotesChildIds: string[];
    permissions: number;

    gridSize: number;

    listElementProps?: any;
    listStyle?: React.CSSProperties;

    createShortcutCard?: (element: object) => void;
};

const QuickNotesScrollableList = (props: Props) => {
    const { quickNotesChildIds, quickNotesRootId, gridSize, listElementProps, listStyle } = props;

    const zoomContextProps = useZoomContext();

    const renderListPlaceholdersProps = useRenderListPlaceholders();
    const { saveElementRenderState, getInitialElementsVisibility, elementPlaceholderMeasurements } =
        renderListPlaceholdersProps;

    const selectionMarqueeListProps = useSelectionMarqueeList();
    const { setListContainerNode, setSelectionMarqueeActive, listContainerNode } = selectionMarqueeListProps;

    const initialElementsVisibility = getInitialElementsVisibility(quickNotesChildIds);

    const dispatch = useDispatch();

    const dispatchRefreshIfModified = (quickNotesRootId: string) =>
        dispatch(refreshIfModified({ boardId: quickNotesRootId }));

    useEffect(() => {
        dispatchRefreshIfModified(quickNotesRootId);

        return () => {
            saveElementRenderState();
        };
    }, []);

    return (
        <div className="QuickNotesScrollableList" data-scrollable>
            <SelectionMarquee
                sectionElementIds={quickNotesChildIds}
                setSelectionMarqueeActive={setSelectionMarqueeActive}
                eventListenerNode={listContainerNode}
                containerWidth={gridSize * (ELEMENT_DEFAULT_WIDTH + 3.9)}
                gridSize={gridSize}
                disabled={quickNotesChildIds.length === 0}
            />
            <OrchestratedList initialRenderSize={INITIAL_RENDER_SIZE} childElementIds={quickNotesChildIds}>
                {(childElementIds: string[], listSize: number) => (
                    <List
                        {...props}
                        {...zoomContextProps}
                        {...renderListPlaceholdersProps}
                        listContainerRef={setListContainerNode}
                        childElementIds={childElementIds}
                        listSize={listSize}
                        listId={quickNotesRootId}
                        currentBoardId={quickNotesRootId}
                        inListClass={IN_LIST_MODE.IN_LIST_QUICK_NOTES}
                        placeholderComponent={<QuickNotesEmptyPlaceholder />}
                        isEditable
                        listCanDropFn={quickNotesCanDrop}
                        listElementProps={{
                            ...listElementProps,
                            initialElementsVisibility,
                            elementPlaceholderMeasurements,
                        }}
                        style={listStyle}
                    />
                )}
            </OrchestratedList>
        </div>
    );
};

export default QuickNotesScrollableList;
